


























import Component from "vue-class-component";
import { Route } from "vue-router";

import { RingLoader } from "vue-spinners-css";

import { PatientDetailsViewModel } from "@/models";
import _auth from "@/utils/auth";
import _gateway from "@/utils/gateway";
import { getFullName, sortByFullName } from "@/utils/names";
import { BaseRootPageComponent } from "@/views/BaseRootPageComponent";
import { EventBus, EventNames } from "@/utils/eventBus";
import { PatientTab } from "@/plugins/native-app";
import { updateGTMGlobalVariables } from "@/utils/gtm";

@Component({
  components: { RingLoader }
})
export default class Patient extends BaseRootPageComponent {
  static readonly ERROR_PATIENT_DOES_NOT_EXIST = "Error: Patient does not exist";

  // Use a class property for dependencies so unit tests can override them
  auth = _auth;
  gateway = _gateway;

  isDestroyed = false;

  loading = true;
  patient: PatientDetailsViewModel = {} as PatientDetailsViewModel;

  get activeNotificationCount() {
    // Need to handle null notifications in case the patient wasn't loaded (e.g. 404)
    return (this.patient.notifications ?? []).filter(n => n.isActive).length;
  }

  get isOnMessagesView() {
    return this.getPatientTab() == PatientTab.Messages;
  }

  get isOnInfusionView() {
    return this.getPatientTab() == PatientTab.Infusion;
  }

  async created() {
    // updateGTMGlobalVariables(this);

    try {
      await this.initialize(parseInt(this.$route.params.id));

      // Subscribe after loading the patient, so that we don't refresh while loading the patient
      this.subscribeToEvents();
    } catch (error) {
      if (error === Patient.ERROR_PATIENT_DOES_NOT_EXIST) {
        this.$router.replace("/my-patients");
      } else {
        throw error;
      }
    }
  }

  subscribeToEvents() {
    // If the component has already been destroyed (e.g. the user navigated away while the initial data was being
    // loaded), don't register for the data refresh event otherwise, it will never be removed and will keep running
    // until the user reloads the webpage.
    if (this.isDestroyed) return;

    EventBus.$on(EventNames.DataRefresh, this.refreshData);
  }

  beforeDestroy() {
    EventBus.$off(EventNames.DataRefresh, this.refreshData);
    this.isDestroyed = true;
  }

  async refreshData() {
    // If somehow the component was already destroyed, don't bother refreshing
    if (this.isDestroyed) return;

    // Don't bother trying to refresh if there isn't a patient.id yet (due to patient not being loaded yet)
    if (!this.patient?.id) return;

    try {
      await this.loadPatient(this.patient.id);
    } catch (error) {
      // If the patient does not exist, redirect the user to the my-patients page
      if (error === Patient.ERROR_PATIENT_DOES_NOT_EXIST) {
        this.$router.replace("/my-patients");
      } else {
        throw error;
      }
    }
  }

  // This is called if the component is being reused to render a different patient. E.g. user clicks on notification
  // in mobile app and jumps from one patient to another
  async beforeRouteUpdate(to: Route, from: Route, next: Function) {
    // Don't bother reloading if the user is just switching tabs and the patient hasn't changed
    const patientId = parseInt(to.params.id);
    try {
      if (patientId !== this.patient?.id) {
        await this.initialize(patientId);
      }

      // Broadcast even if the patient doesn't change because the tab may have changed
      // Need to send the new route, since it hasn't finished routing yet
      this.broadcastNavigatedToPatientAndTab(to);

      next();
    } catch (error) {
      // If the patient does not exist, redirect the user to the my-patients page
      if (error === Patient.ERROR_PATIENT_DOES_NOT_EXIST) {
        next("/my-patients");
      } else {
        throw error;
      }
    }
  }

  async beforeRouteLeave(to: Route, from: Route, next: Function) {
    // Let the native app know that the user navigated off the patient details page
    this.$nativeApp.navigatedToPatient(0);
    next();
  }

  async initialize(patientId: number) {
    this.loading = true;

    this.setTitle("");
    await this.loadPatient(patientId);
    this.broadcastNavigatedToPatientAndTab();

    this.loading = false;
  }

  /**
   * Lets the mobile app know that the user navigate in case there are any notifications that need to be cleared
   */
  broadcastNavigatedToPatientAndTab(route?: Route) {
    const tab = this.getPatientTab(route);
    this.$nativeApp.navigatedToPatient(this.patient.id, tab);
  }

  getPatientTab(route?: Route) {
    if (!route) route = this.$route;

    if (route.path.endsWith("/messages")) return PatientTab.Messages;
    else if (route.path.endsWith("/infusion")) return PatientTab.Infusion;
    else return PatientTab.Info;
  }

  async loadPatient(patientId: number) {
    try {
      this.patient = await this.gateway.getPatient(patientId);
    } catch (error) {
      if ((error as any).status === 404) {
        throw Patient.ERROR_PATIENT_DOES_NOT_EXIST;
      } else {
        throw error;
      }
    }

    // Sort the watchers alphabetically (sort by full name so they are sorted for the modal too)
    this.patient.watchers = this.patient.watchers.sort(sortByFullName);

    // Update the title of the route
    this.setTitle(getFullName(this.patient));
  }
}
